import React, { createContext, ReactNode, useContext, useReducer } from 'react';

import { GlobalContext } from '../../../../../../store';
import { IAward } from '../../../../../../store/admin/eventAwards/store';
import { IAdminFlight, IFlightObject } from '../../../../../../store/admin/eventFlights/store';
import { IHoleResult } from '../../../../../../store/admin/holeResults/store';
import {
  IFormat,
  IMetaFormat,
  TIFormatPrefix
} from '../../../../../../store/admin/roundFormats/store';
import { IResult } from '../../../../../../store/admin/roundResults/store';
import { ScoreType } from '../../../../../../store/admin/eventFormats/store';
import { IUserAwards } from '../../../../../../store/admin/roundFormatAwards/store';
import { IAdminTeamResult } from '../../../../../../store/admin/teamResults/store';

import { sortBy } from '../../../../../../contrib/sortBy';
import { getResultFieldName } from '../../ResultsTable/Utils/getResultFieldName';
import { ISetCombinations, setCombinations } from './BoundActionCreators/setCombinations';
import {
  ISetHorseRaceEliminatedResults,
  setHorseRaceEliminatedResults
} from './BoundActionCreators/setHorseRaceEliminatedResults';
import { ISetSkinsResults, setSkinsResults } from './BoundActionCreators/setSkinsResults';
import { ISetGBNResults, setGBNResults } from './BoundActionCreators/setGBNResults';
import { ISetCTPResults, setCTPResults } from './BoundActionCreators/setCTPResults';
import { filterCombinations } from './filterCombinations';

export interface ISkinsResults {
  [hole: number]: IHoleResult[];
}

export interface IGBNResults {
  [hole: number]: IHoleResult[];
}

type IActions =
  | { type: 'filterCombinations' }
  | { type: 'selectCombination'; value: ICombination }
  | { type: 'selectFlight'; value: IAdminFlight | undefined }
  | { type: 'selectFormat'; value: number | undefined }
  | { type: 'selectScoreType'; value: number | undefined }
  | { type: 'selectTeamFormat'; value: TTeamFormatPrefix | undefined }
  | { type: 'resetAwardToUpdate' }
  | { type: 'setAwardToUpdate'; value: { userId: number; award: IAward } }
  | { type: 'setCombinations'; value: ICombination[] }
  | { type: 'setEventFormatsList'; value: IMetaFormats }
  | { type: 'setFlights'; value: IFlightObject }
  | { type: 'setFormatsList'; value: IFormats }
  | { type: 'setGBNResults'; value: IGBNResults }
  | { type: 'setHorseRaceEliminatedResults'; value: IHorseRaceEliminatedResults }
  | { type: 'setResults'; value: IResult[] }
  | { type: 'setRoundFormatsList'; value: IMetaFormats }
  | { type: 'setSelectedRoundFormat'; value: IMetaFormat | undefined }
  | { type: 'setSkinsResults'; value: ISkinsResults }
  | { type: 'setCTPResults'; value: ISkinsResults }
  | { type: 'setTeamResults'; value: IAdminTeamResult[] }
  | { type: 'setUserAwards'; value: IUserAwards }
  | { type: 'sortResults' }
  | { type: 'toggleEditMode' };

export interface IMetaFormats {
  [formatId: number]: IMetaFormat;
}

export interface IFormats {
  [formatId: number]: IFormat;
}

export interface IHorseRaceEliminatedResults {
  true: IResult[];
  false: IResult[];
}

export interface ICombination {
  aggregation: number;
  flight: IAdminFlight | null;
  format: IMetaFormat;
  hasAwards: boolean;
  id: number;
  scoreType: ScoreType;
}

export type TTeamFormatPrefix = 'teamStrokeTotal' | 'teamStrokeBetterball' | 'gbn';

interface IAssignResultsState {
  awardsToUpdate: IUserAwards;
  combinations: ICombination[] | undefined;
  editMode: boolean;
  eventFormatsList: IMetaFormats;
  filteredCombinations: ICombination[] | undefined;
  flights: IFlightObject;
  formatsList: IFormats;
  horseRaceEliminatedResults: IHorseRaceEliminatedResults;
  results: IResult[];
  roundFormatsList: IMetaFormats;
  selectedCombination: ICombination | undefined;
  selectedFlight: IAdminFlight | undefined;
  selectedFormat: IFormat | undefined;
  selectedRoundFormat: IMetaFormat | undefined;
  selectedScoreType: ScoreType | undefined;
  selectedTeamFormat: TTeamFormatPrefix | undefined;
  skinsResults: ISkinsResults;
  ctpResults: ISkinsResults;
  gbnResults: IGBNResults;
  sortedResults: IResult[];
  teamResults: IAdminTeamResult[];
  userAwards: IUserAwards;
}

const initialState: IAssignResultsState = {
  awardsToUpdate: {},
  combinations: undefined,
  ctpResults: {},
  editMode: false,
  eventFormatsList: {},
  filteredCombinations: undefined,
  flights: {},
  formatsList: {},
  gbnResults: {},
  horseRaceEliminatedResults: {
    false: [],
    true: []
  },
  results: [],
  roundFormatsList: {},
  selectedCombination: undefined,
  selectedFlight: undefined,
  selectedFormat: undefined,
  selectedRoundFormat: undefined,
  selectedScoreType: 0,
  selectedTeamFormat: undefined,
  skinsResults: {},
  sortedResults: [],
  teamResults: [],
  userAwards: {}
};

export const scoreTypeOptions = [{ value: '1', label: 'GROSS' }, { value: '2', label: 'NET' }];

export type IStateDispatchActions<T> = (
  state: IAssignResultsState,
  dispatch: (action: IActions) => void
) => T;

interface IDispatch {
  dispatch: (action: IActions) => void;
  actions: IBoundActionCreators;
}

const AssignResultsStateContext = createContext<IAssignResultsState | undefined>(undefined);
const AssignResultsDispatchContext = createContext<IDispatch | undefined>(undefined);

const AssignResultsReducer = (
  state: IAssignResultsState,
  action: IActions
): IAssignResultsState => {
  switch (action.type) {
    case 'selectFormat': {
      const formatToSelectId = action.value;

      const selectedRoundFormat = Object.values(state.roundFormatsList).find(
        roundFormat => roundFormat.format.id === formatToSelectId
      );

      const selectedFormat =
        formatToSelectId === undefined ? formatToSelectId : state.formatsList[formatToSelectId];

      const selectedScoreType =
        selectedRoundFormat && selectedRoundFormat.scoreTypes.length === 1
          ? selectedRoundFormat.scoreTypes[0]
          : state.selectedScoreType;

      return {
        ...state,
        selectedFormat,
        selectedRoundFormat,
        selectedScoreType
      };
    }

    case 'selectScoreType': {
      return { ...state, selectedScoreType: action.value };
    }

    case 'selectFlight': {
      return { ...state, selectedFlight: action.value };
    }

    case 'toggleEditMode': {
      return { ...state, editMode: !state.editMode };
    }

    case 'setUserAwards': {
      return { ...state, userAwards: action.value };
    }

    case 'resetAwardToUpdate': {
      return { ...state, awardsToUpdate: {} };
    }

    case 'setAwardToUpdate': {
      const { award: awardToUpdate, userId } = action.value;

      if (awardToUpdate.id < 0) {
        delete awardToUpdate.id;
      }
      if (!awardToUpdate.team) {
        delete awardToUpdate.team;
      }

      const userAwards = state.awardsToUpdate[userId];
      const theresAwardForThisUser = !!userAwards;

      return {
        ...state,
        awardsToUpdate: {
          ...state.awardsToUpdate,
          [userId]: theresAwardForThisUser
            ? [...userAwards.filter(award => award.id !== awardToUpdate.id), awardToUpdate]
            : [awardToUpdate]
        }
      };
    }

    case 'setResults': {
      return { ...state, results: action.value };
    }

    case 'setTeamResults': {
      return { ...state, teamResults: action.value };
    }

    case 'selectTeamFormat': {
      return { ...state, selectedTeamFormat: action.value };
    }

    case 'setCTPResults': {
      return { ...state, ctpResults: action.value };
    }

    case 'setSkinsResults': {
      return { ...state, skinsResults: action.value };
    }

    case 'setGBNResults': {
      return { ...state, gbnResults: action.value };
    }

    case 'setRoundFormatsList': {
      return { ...state, roundFormatsList: action.value };
    }

    case 'setEventFormatsList': {
      return { ...state, eventFormatsList: action.value };
    }

    case 'setFormatsList': {
      return { ...state, formatsList: action.value };
    }

    case 'setFlights': {
      return { ...state, flights: action.value };
    }

    case 'setCombinations': {
      return { ...state, combinations: action.value };
    }

    case 'setSelectedRoundFormat': {
      return {
        ...state,
        selectedRoundFormat: action.value
      };
    }

    case 'setHorseRaceEliminatedResults': {
      return { ...state, horseRaceEliminatedResults: action.value };
    }

    case 'filterCombinations': {
      const { combinations, selectedScoreType, selectedFlight, selectedFormat } = state;

      if (!combinations) {
        return state;
      }

      const filteredCombinations = combinations.filter(
        filterCombinations({ selectedScoreType, selectedFlight, selectedFormat })
      );

      return {
        ...state,
        filteredCombinations
      };
    }

    case 'selectCombination': {
      return { ...state, selectedCombination: action.value };
    }

    case 'sortResults': {
      const { selectedCombination, results } = state;

      if (selectedCombination && results.length > 0) {
        const {
          format: {
            considerFlights,
            scoringFormat,
            format: { prefix: selectedFormatPrefix }
          },
          scoreType: selectedScoreType
        } = selectedCombination;

        const formatIsStableford = scoringFormat === 1;

        const isHorse = selectedFormatPrefix === 'horse';

        const processedPrefix = isHorse
          ? (`${selectedFormatPrefix}${considerFlights ? 'Flight' : 'Round'}` as TIFormatPrefix)
          : selectedFormatPrefix;

        const positionFieldName = getResultFieldName(
          processedPrefix,
          selectedScoreType,
          isHorse ? 'Hole' : 'Position',
          false,
          formatIsStableford
        );

        const sortedResults = sortBy(state.results, isHorse ? 'dec' : 'asc', positionFieldName);

        return {
          ...state,
          sortedResults
        };
      } else {
        return { ...state, sortedResults: results };
      }
    }
  }
};

interface CountProviderProps {
  children: ReactNode;
}

interface IBoundActionCreators {
  setCombinations: ISetCombinations;
  setHorseRaceEliminatedResults: ISetHorseRaceEliminatedResults;
  setSkinsResults: ISetSkinsResults;
  setCTPResults: ISetCTPResults;
  setGBNResults: ISetGBNResults;
}

const AssignResultsProvider = ({ children }: CountProviderProps) => {
  const [state, dispatch] = useReducer(AssignResultsReducer, initialState as never);

  const actions: IBoundActionCreators = {
    setCTPResults: setCTPResults(state, dispatch),
    setCombinations: setCombinations(state, dispatch),
    setGBNResults: setGBNResults(state, dispatch),
    setHorseRaceEliminatedResults: setHorseRaceEliminatedResults(state, dispatch),
    setSkinsResults: setSkinsResults(state, dispatch)
  };

  const {} = useContext(GlobalContext);

  return (
    <AssignResultsStateContext.Provider value={state}>
      <AssignResultsDispatchContext.Provider value={{ dispatch, actions }}>
        {children}
      </AssignResultsDispatchContext.Provider>
    </AssignResultsStateContext.Provider>
  );
};

const useAssignResultsState = () => {
  const context = useContext(AssignResultsStateContext);

  if (context === undefined) {
    throw new Error('useAssignResultsState must be used within a AssignResultsProvider');
  }
  return context;
};

const useAssignResultsDispatch = () => {
  const context = useContext(AssignResultsDispatchContext);

  if (context === undefined) {
    throw new Error('useAssignResultsDispatch must be used within a AssignResultsProvider');
  }
  return context;
};

export { AssignResultsProvider, useAssignResultsState, useAssignResultsDispatch };
